import React, { useCallback, useEffect, useState } from "react";
import BillingAccountsSettings from "../superAdmin/BillingAccountsSettings";
import {
  getBillingAccounts,
  updateBillingAccounts,
} from "../../services/clientAdminService";
import Toast from "../ui/Toast";

const BillingAccountsModal = ({ isOpen, onClose, refreshDropdownValues }) => {
  const [providers, setProviders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ message: null, type: "success" });

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getBillingAccounts();
      setProviders(response.data?.providers || []);
      setAccounts(response.data?.accounts || []);
    } catch (loadError) {
      setError(loadError.message || "Failed to load billing accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    loadAccounts();
  }, [isOpen, loadAccounts]);

  const closeModal = useCallback(() => {
    if (saving) return;
    setError("");
    onClose();
  }, [onClose, saving]);

  const saveChanges = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await updateBillingAccounts({ providers, accounts });
      setProviders(response.data?.providers || []);
      setAccounts(response.data?.accounts || []);
      onClose();
      setToast({
        message: "Billing accounts updated successfully.",
        type: "success",
      });
      if (refreshDropdownValues) await refreshDropdownValues();
    } catch (saveError) {
      setError(saveError.message || "Failed to save billing accounts");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeModal, isOpen]);

  return (
    <>
      <div className="fixed top-4 right-4 z-[10000]">
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: null, type: "success" })}
        />
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-[#1c1c1a]/25 p-5 backdrop-blur-[10px]"
          style={{ WebkitBackdropFilter: "blur(10px)" }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="billing-accounts-modal-title"
        >
          <div className="relative z-[1] flex max-h-[90vh] w-full max-w-7xl flex-col overflow-hidden rounded-[12px] bg-white shadow-[0_24px_80px_rgba(28,28,26,0.28)]">
            <div className="flex flex-shrink-0 items-start justify-between gap-4 border-b border-[var(--line,#e4e2dc)] px-6 py-4">
              <div className="min-w-0">
                <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#a51c1c]">
                  New billing account
                </p>
                <h2
                  id="billing-accounts-modal-title"
                  className="mt-1 text-[22px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]"
                >
                  Add Provider / Billing Account
                </h2>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="shrink-0 rounded-md p-1 text-[26px] leading-none text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] hover:text-[var(--ink,#1c1c1a)] disabled:opacity-50"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              {loading ? (
                <div className="py-16 text-center text-sm text-gray-500">
                  Loading billing accounts…
                </div>
              ) : (
                <BillingAccountsSettings
                  providers={providers}
                  accounts={accounts}
                  onProvidersChange={setProviders}
                  onAccountsChange={(next) => {
                    setAccounts(next);
                    setError("");
                  }}
                  loading={saving}
                />
              )}
            </div>

            {error && (
              <div className="flex-shrink-0 border-t border-red-100 bg-red-50 px-6 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex flex-shrink-0 justify-end gap-2 border-t border-[var(--line,#e4e2dc)] bg-white px-6 py-3">
              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="inline-flex items-center justify-center rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveChanges}
                disabled={loading || saving}
                className="inline-flex items-center justify-center rounded-md bg-[#a51c1c] px-4 py-2 text-[13px] font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BillingAccountsModal;
